import { Util } from 'src/shared/util/util';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewEncapsulation,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { DateTime as LuxonDateTime } from 'luxon';
import {
  Properties,
  Property,
  Job,
  ClickAction,
  ClickTargets,
  Period,
  AvailabilityRequest,
  EventTypes,
} from 'src/models/schedule-weekly-view.model';
import { BookJob } from 'src/providers/book-job/book-job';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Pros } from 'src/providers/pros/pros';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { BookService } from 'src/shared/services/book.service';
import { SelectCategoryPage } from 'src/pages/select-category/select-category';
import { BookJobPage } from 'src/pages/booking/book-job/book-job';
import { PlansPage } from 'src/pages/plans/plans';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Schedule } from 'src/providers/schedule/schedule';
import { WindowService } from 'src/shared/providers/window.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

@Component({
    templateUrl: 'schedule-day-view.html',
    styleUrls: ['./schedule-day-view.scss'],
    selector: 'tidy-schedule-day-view',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ScheduleDayViewComponent implements OnChanges, AfterViewInit {
  @Input() isMobileResolution: boolean;
  @Input() selectedPeriodRange: Period;
  @Input() properties: Properties;
  @Input() showAboutThisPage: boolean;
  @Output() clickAction = new EventEmitter<ClickAction>();

  today = LuxonDateTime.local().toISODate();
  hasPrivatePros: boolean;
  allProperties: Properties;
  wasCardClicked = false;
  @ViewChild('scrollableContainer', { static: true })
  scrollableContainer: ElementRef;

  // Time slots from 6am to 10pm
  timeSlots: string[] = [];

  constructor(
    public bookJobService: BookJob,
    private changeDetectorRef: ChangeDetectorRef,
    private client: Client,
    private navCtrl: CustomNavController,
    private pros: Pros,
    public util: Util,
    public schedule: Schedule,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    public windowService: WindowService,
    public onboardingProvider: OnboardingProvider,
    private bookService: BookService
  ) {
    this.generateTimeSlots();
  }

  async ngAfterViewInit() {
    if (!this.showAboutThisPage) {
      this.hasPrivatePros = await this.pros.checkIfHasPrivatePro();
    }
    this.scrollableContainer.nativeElement.addEventListener(
      'scroll',
      (event: any) => {
        const element = event.target;
        const dynamicThreshold = window.innerHeight * 0.1;
        const atBottom =
          Math.abs(
            element.scrollHeight - element.scrollTop - element.clientHeight
          ) < dynamicThreshold;
        if (atBottom) {
          this.showMore();
        }
      }
    );
    // Scroll to default time position after view is ready
    setTimeout(() => {
      this.scrollToDefaultTime();
    }, 100);
  }

  ngOnChanges(): void {
    this.allProperties = this.properties;
    this.properties = this.properties.slice(0, 20);

    // Add classes to jobs for better performance
    this.addClassesToJobs();

    // Scroll to default time when properties change
    if (this.scrollableContainer?.nativeElement) {
      setTimeout(() => {
        this.scrollToDefaultTime();
      }, 100);
    }
  }

  generateTimeSlots(): void {
    // Generate time slots from 6am to 10pm
    for (let hour = 6; hour <= 22; hour++) {
      const time = hour > 12 ? `${hour - 12}pm` : hour === 12 ? '12pm' : `${hour}am`;
      this.timeSlots.push(time);
    }
  }

  showMore(): void {
    if (this.properties.length >= this.allProperties.length) {
      return;
    }
    this.properties.push(
      ...this.allProperties.slice(
        this.properties.length,
        this.properties.length + 10
      )
    );

    // Add classes to newly added jobs
    this.addClassesToJobs();
  }

  getTimeSlotHeader(slot: string): string {
    return slot;
  }

  getCurrentDate(): string {
    const currentDate = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate);
    return currentDate.toFormat('EEEE, MMMM d, yyyy');
  }

  hasJobAtTimeSlot(property: Property, timeSlot: string): boolean {
    return !!this.getJobsAtTimeSlot(property, timeSlot).length;
  }

  getJobsAtTimeSlot(property: Property, timeSlot: string): Job[] {
    const slotHour = this.parseTimeSlotHour(timeSlot);

    return property.filteredJobs.filter((job: any, index) => {
      // Check if job is on the selected day
      if (job.startDate !== this.selectedPeriodRange.startDate) {
        return false;
      }

      job.paddingTop = this.getPaddingTop(job, property, 'jobs', index);
      let jobHour = this.parseJobHour(job.time);

      if (job.isJobRequest && job.booking?.startTimeNoEarlierThan) {
        const bookingHour = this.parseJobHour(job.booking.startTimeNoEarlierThan);
        // Use the earlier time (smaller hour value)
        if (bookingHour !== -1 && (jobHour === -1 || bookingHour < jobHour)) {
          jobHour = bookingHour;
        }
      }

      return jobHour === slotHour;
    });
  }

  getReservationsAtTimeSlot(property: Property, timeSlot: string): any[] {
    const slotHour = this.parseTimeSlotHour(timeSlot);
    const givenDate = this.selectedPeriodRange.startDate;
    const givenDateTime = LuxonDateTime.fromISO(givenDate);

    return property.filteredReservations.filter((reservation: any) => {
      const reservationStartDate = LuxonDateTime.fromISO(reservation.period.startDate);
      const reservationEndDate = LuxonDateTime.fromISO(reservation.period.endDate);
      const isGivenDateWithinReservationPeriod = givenDateTime >= reservationStartDate && givenDateTime <= reservationEndDate;

      if (!isGivenDateWithinReservationPeriod) {
        return false;
      }

      const parsedStartHour = Number(reservation.check_in_time.split(':')[0]);

      // If reservation starts on the given day, show it at its actual start time
      if (givenDateTime.hasSame(reservationStartDate, 'day')) {
        return parsedStartHour === slotHour;
      }

      // If reservation started in the past but is still active on given day, show it on first time slot
      if (givenDateTime > reservationStartDate && givenDateTime <= reservationEndDate) {
        return slotHour === 6; // First time slot (6am)
      }

      return false;
    });
  }

  parseTimeSlotHour(timeSlot: string): number {
    // Parse "8am", "12pm", "1pm" etc. to 24-hour format
    const match = timeSlot.match(/(\d+)(am|pm)/);
    if (!match) {return -1;}

    let hour = parseInt(match[1]);
    const isPM = match[2] === 'pm';

    if (isPM && hour !== 12) {
      hour += 12;
    } else if (!isPM && hour === 12) {
      hour = 0;
    }

    return hour;
  }

  parseJobHour(jobTime: string): number {
    if (!jobTime) {return -1;}

    // Parse "9:00am", "2:30pm" etc.
    const match = jobTime.match(/(\d+):(\d+)(am|pm)/i);
    if (!match) {return -1;}

    let hour = parseInt(match[1]);
    const isPM = match[3].toLowerCase() === 'pm';

    if (isPM && hour !== 12) {
      hour += 12;
    } else if (!isPM && hour === 12) {
      hour = 0;
    }

    return hour;
  }

  isHalfHourJob(job: Job): boolean {
    if (!job) {return false;}

    // Check regular job time
    if (job.time) {
      const match = job.time.match(/(\d+):(\d+)(am|pm)/i);
      if (match) {
        const minutes = parseInt(match[2]);
        return minutes === 30;
      }
    }

    // Check job request booking time
    if (job.isJobRequest && job.booking?.startTimeNoEarlierThan) {
      const match = job.booking.startTimeNoEarlierThan.match(/(\d+):(\d+)(am|pm)/i);
      if (match) {
        const minutes = parseInt(match[2]);
        return minutes === 30;
      }
    }

    return false;
  }

  getJobCardWidth(job: Job): number {
    if (!job) {return 105;} // Default width

    const jobStartMinutes = this.parseTimeToMinutes(job.booking.startTimeNoEarlierThan);
    const jobEndMinutes = this.parseTimeToMinutes(job.booking.endTimeNoLaterThan);

    // Calculate duration in minutes
    const jobDuration = jobEndMinutes - jobStartMinutes;

    // Apply width adjustments for longer durations
    const widthDurations = {
      60: 105,
      90: 210,
      120: 265,
      150: 365,
      180: 420,
      210: 470,
      240: 525,
      270: 580,
      300: 630,
    };

    let widthPercentage = widthDurations[jobDuration];

    // Use predefined widths for common durations
    if (!widthPercentage) {
      // For durations longer than our predefined values, use the last available width
      const lastKey = Number(Object.keys(widthDurations)[Object.keys(widthDurations).length - 1]);
      if (jobDuration > lastKey) {
        widthPercentage = widthDurations[lastKey];
      }
    }

    // Apply late night adjustment (jobs ending after 10pm)
    const willEndAfter10pm = jobEndMinutes > 1200;
    if (willEndAfter10pm) {
      widthPercentage = widthPercentage - 40;
    }

    // Ensure minimum width
    return Math.max(105, widthPercentage);
  }

  getJobCardStyle(job: Job, timeSlot: string): string {
    const width = this.getJobCardWidth(job);
    return `width: ${width}%;`;
  }

    getJobCardClasses(job: Job): string {
    const classes: string[] = [];

    // Add half-hour job class if needed
    if (this.isHalfHourJob(job)) {
      classes.push('half-hour-job');
    }

    return classes.join(' ');
  }

  addClassesToJobs(): void {
    if (this.properties) {
      this.properties.forEach(property => {
        if (property.filteredJobs) {
          property.filteredJobs.forEach(job => {
            // Add classes and width properties to job object for better performance
            (job as any).classes = this.getJobCardClasses(job);
            (job as any).width = this.getJobCardWidth(job);
          });
        }

        // Pre-compute time slot data for better performance
        this.precomputeTimeSlotData(property);

        // Pre-compute next dates for better performance
        this.precomputeNextDates(property);

        // The reservation card popover and its View Details navigation need the property name
        (property as any).displayName = property?.name || property?.address || '';
      });
    }
  }

  precomputeNextDates(property: Property): void {
    // Add next reservation and job dates to property for better performance
    (property as any).nextReservationDateFormatted = this.getNextReservationDate(property);
    (property as any).nextJobDateFormatted = this.getNextJobDate(property);
  }

  precomputeTimeSlotData(property: Property): void {
    // Add time slot data to property for each time slot
    (property as any).timeSlotData = {};

    // Count total jobs for this property on the selected day
    const jobsForDay = property.filteredJobs.filter(
      (job: any) => job.startDate === this.selectedPeriodRange.startDate
    );
    (property as any).jobCountForDay = jobsForDay.length;

    // Track if property has any reservation for this day
    let hasReservationForDay = false;

    this.timeSlots.forEach(timeSlot => {
      const hasJob = this.hasJobAtTimeSlot(property, timeSlot);
      const jobs = hasJob ? this.getJobsAtTimeSlot(property, timeSlot) : [];
      const reservations = this.getReservationsAtTimeSlot(property, timeSlot);
      const hasReservation = reservations.length > 0;
      const isCurrentTime = this.isCurrentTimeSlot(timeSlot);
      const anchorId = this.getTimeSlotAnchorId(timeSlot);

      if (hasReservation) {
        hasReservationForDay = true;
      }

      (property as any).timeSlotData[timeSlot] = {
        hasJob,
        jobs,
        hasReservation,
        reservations,
        isCurrentTime,
        anchorId
      };
    });

    (property as any).hasReservationForDay = hasReservationForDay;
  }

  getPaddingTop(
    event: Job | AvailabilityRequest,
    property: Property,
    eventType: EventTypes,
    index: number
  ): number {
    let paddingTop = 25;
    if (index > 0) {
      paddingTop = 0;
      return paddingTop;
    }

    // TODO add better logic here to check the padding top for the job
    return paddingTop;
  }

  parseTimeToMinutes(timeString: string): number {
    if (!timeString) {return -1;}

    // Parse "9:30am", "2:30pm" etc. to total minutes since midnight
    const match = timeString.match(/(\d+):(\d+)(am|pm)/i);
    if (!match) {return -1;}

    let hour = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const isPM = match[3].toLowerCase() === 'pm';

    if (isPM && hour !== 12) {
      hour += 12;
    } else if (!isPM && hour === 12) {
      hour = 0;
    }

    return hour * 60 + minutes;
  }

  emitClickAction(
    target: ClickTargets,
    property: Property,
    eventType?: EventTypes,
    event?: Job | any
  ): void {
    localStorage.setItem('addressId', property.id.toString());
    this.wasCardClicked = true;

    let finalEventType = eventType || 'jobs';
    if (!eventType && event && event.period) {
      finalEventType = 'reservations';
    }

    this.clickAction.emit({
      target,
      eventType: finalEventType,
      property,
      event: event as any,
    });
  }

  async goToBookJob(timeSlot: string, property: Property) {
    this.bookJobService.clearBookJobStorage();

    const wasCardClicked = await new Promise((resolve) =>
      setTimeout(() => {
        if (this.wasCardClicked) {
          resolve(this.wasCardClicked);
          return;
        }
        resolve(null);
      }, 100)
    );

    if (wasCardClicked) {
      this.wasCardClicked = false;
      return;
    }

    // Convert time slot to 12-hour format for booking (e.g., "8am" -> "8:00am", "2pm" -> "2:00pm")
    const providedStartTime = timeSlot.replace(/(\d+)(am|pm)/, '$1:00$2');

    await this.storage.save('bookJobBackPage', 'schedule');
    await this.client.getClientInfo();
    const addresses = await this.client.getMoreDetailAddresses();
    const addressId = property.id;
    localStorage.setItem('addressId', addressId.toString());
    const address = addresses.find((ad) => ad.id === addressId);
    const bookingType = 'add_job';
    // Convert date from ISO format (yyyy-MM-dd) to MM-dd-yyyy format expected by book-job
    const dateForBooking = LuxonDateTime.fromISO(this.selectedPeriodRange.startDate).toFormat('MM-dd-yyyy');

    const params = {
      address,
      bookingType,
      providedStartDate: dateForBooking,
      providedStartTime,
    };
    await this.bookService.goToBookJob(params);
  }

  private navigateToPlansPage(address: any, plans: any, addressFilter: any): void {
    const plansParams = { address, plans, addressFilter };
    if (!this.isMobileResolution) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', plansParams);
      this.rightSidePanelService.openRightSidePanel(PlansPage);
    } else {
      this.navCtrl.navigateForward('plans', plansParams);
    }
  }

  trackByFn(index, item) {
    return item.id;
  }

  isCurrentTimeSlot(timeSlot: string): boolean {
    const now = LuxonDateTime.local();
    const currentHour = now.hour;
    const slotHour = this.parseTimeSlotHour(timeSlot);

    // Check if it's today and current hour
    return this.selectedPeriodRange.startDate === this.today && currentHour === slotHour;
  }

  getNextReservationDate(property: Property): string {
    if (!property.nextReservationDate) {return '';}

    const date = LuxonDateTime.fromISO(property.nextReservationDate);
    return date.toFormat('MMM d');
  }

  getNextJobDate(property: Property): string {
    if (!property.nextJobDate) {return '';}

    const date = LuxonDateTime.fromISO(property.nextJobDate);
    return date.toFormat('MMM d');
  }

  getReservationCardStyle(reservation: any, timeSlot: string): string {
    const slotHour = this.parseTimeSlotHour(timeSlot);
    const givenDate = this.selectedPeriodRange.startDate;
    const givenDateTime = LuxonDateTime.fromISO(givenDate);
    const reservationStartDate = LuxonDateTime.fromISO(reservation.period.startDate);
    const reservationEndDate = LuxonDateTime.fromISO(reservation.period.endDate);
    const isGivenDateNotStartOrEndDate = givenDateTime > reservationStartDate && givenDateTime < reservationEndDate;
    if (isGivenDateNotStartOrEndDate) {
      return 'width: 1800%;';
    }

    const reservationEndsAtGivenDate = reservationEndDate.hasSame(givenDateTime, 'day');
    if (!reservationEndsAtGivenDate) {
      const remainingSlots = 22 - slotHour;
      const widthPercentage = remainingSlots * 105;
      return `width: ${widthPercentage}%;`;
    }
    const endHour = Number(reservation.check_out_time.split(':')[0]);
    const widthPercentage = 105 * (endHour - slotHour);
    return `width: ${widthPercentage}%;`;
  }

  scrollToDefaultTime(): void {
    if (!this.scrollableContainer?.nativeElement) {return;}

    let targetTimeSlot = '';

    if (this.selectedPeriodRange.startDate === this.today) {
      // If viewing today, scroll to current time
      const now = LuxonDateTime.local();
      const currentHour = now.hour;
      // Find the closest time slot to current time
      for (const timeSlot of this.timeSlots) {
        const slotHour = this.parseTimeSlotHour(timeSlot);
        if (slotHour >= currentHour) {
          targetTimeSlot = timeSlot;
          break;
        }
      }

      // If current time is after all slots, use the last slot
      if (!targetTimeSlot) {
        targetTimeSlot = this.timeSlots[this.timeSlots.length - 1];
      }
    } else {
      // If not today, scroll to the first scheduled job time
      let earliestJobHour = 24; // Start with impossible hour

      for (const property of this.properties) {
        for (const job of property.filteredJobs) {
          if (job.startDate === this.selectedPeriodRange.startDate) {
            // For job requests, consider both job.time and booking.startTimeNoEarlierThan
            // Use the earlier time to determine the earliest job
            let jobHour = this.parseJobHour(job.time);

            if (job.isJobRequest && job.booking?.startTimeNoEarlierThan) {
              const bookingHour = this.parseJobHour(job.booking.startTimeNoEarlierThan);
              // Use the earlier time (smaller hour value)
              if (bookingHour !== -1 && (jobHour === -1 || bookingHour < jobHour)) {
                jobHour = bookingHour;
              }
            }

            if (jobHour !== -1 && jobHour < earliestJobHour) {
              earliestJobHour = jobHour;
            }
          }
        }
      }

      // Find the time slot that matches or is closest to the earliest job
      if (earliestJobHour < 24) {
        for (const timeSlot of this.timeSlots) {
          const slotHour = this.parseTimeSlotHour(timeSlot);
          if (slotHour >= earliestJobHour) {
            targetTimeSlot = timeSlot;
            break;
          }
        }
      }

      // If no jobs found or they're before our time range, default to 8am
      if (!targetTimeSlot) {
        targetTimeSlot = '8am';
      }
    }

    // Use anchor-based scrolling
    if (targetTimeSlot) {
      this.scrollToTimeSlot(targetTimeSlot);
    }
  }

  /**
   * Generate anchor ID for a time slot
   */
  getTimeSlotAnchorId(timeSlot: string): string {
    return `time-slot-${timeSlot.replace(/[^a-zA-Z0-9]/g, '')}`;
  }

  /**
   * Scroll to a specific time slot using anchor-based scrolling
   */
  scrollToTimeSlot(timeSlot: string): void {
    if (!this.scrollableContainer?.nativeElement) {return;}

    // Create anchor ID for the time slot
    const anchorId = this.getTimeSlotAnchorId(timeSlot);
    // Try to find the anchor element
    const anchorElement = document.getElementById(anchorId);

    if (anchorElement) {
      anchorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });

      const containerRect = this.scrollableContainer.nativeElement.getBoundingClientRect();
      const anchorRect = anchorElement.getBoundingClientRect();

      const scrollLeft = this.scrollableContainer.nativeElement.scrollLeft +
                        (anchorRect.left - containerRect.left) -
                        (containerRect.width / 2) +
                        (anchorRect.width / 2);

      this.scrollableContainer.nativeElement.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth'
      });
    } else {
      const timeSlotIndex = this.timeSlots.indexOf(timeSlot);
      if (timeSlotIndex !== -1) {
        const propertyColumnWidth = this.windowService.isDesktopRes ? 250 : 120;
        const timeSlotWidth = 100;
        const scrollLeft = propertyColumnWidth + (timeSlotIndex * timeSlotWidth) - (timeSlotWidth * 2);

        this.scrollableContainer.nativeElement.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    }
  }

  /**
   * Public method to manually trigger scrolling to a specific time slot
   * Useful when switching to day view mode from other views
   */
  scrollToCurrentTime(): void {
    setTimeout(() => {
      this.scrollToDefaultTime();
    }, 100);
  }

  /**
   * Public method to scroll to a specific time slot
   * @param timeSlot - The time slot to scroll to (e.g., "3pm", "9am")
   */
  scrollToSpecificTime(timeSlot: string): void {
    if (this.timeSlots.includes(timeSlot)) {
      this.scrollToTimeSlot(timeSlot);
    } else {
        // Intentionally empty
    }
  }
}
